import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Freeze Frame — FTUE demo (3 essais progressive intro).
 *
 * Spec : wiki/games/freeze-frame.md §FTUE
 *   E1 : Go-only — « Tape sur X. » Apprend la commande Go.
 *   E2 : NoGo introduit — « Si Y, NE tape PAS. »
 *   E3 : vitesse normale — calibration baseline.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'freeze_frame.demo.step1_title',
        hintKey: 'freeze_frame.demo.step1_hint',
        glyph: 'X',
        kind: 'go',
        durationMs: 1100,
    },
    {
        idx: 2,
        titleKey: 'freeze_frame.demo.step2_title',
        hintKey: 'freeze_frame.demo.step2_hint',
        glyph: 'Y',
        kind: 'nogo',
        durationMs: 900,
    },
    {
        idx: 3,
        titleKey: 'freeze_frame.demo.step3_title',
        hintKey: 'freeze_frame.demo.step3_hint',
        glyph: 'X',
        kind: 'go',
        durationMs: 700,
    },
];
const FIELD = 240;
const AWAITING_MS = 700;
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('showing');
    const [tapped, setTapped] = useState(false);
    const showTimerRef = useRef(null);
    const awaitingTimerRef = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const wasGo = step.kind === 'go';
    const correct = tapped ? wasGo : !wasGo;
    useEffect(() => {
        if (phase !== 'showing')
            return;
        showTimerRef.current = setTimeout(() => {
            setPhase('awaiting');
            awaitingTimerRef.current = setTimeout(() => {
                setPhase('feedback');
            }, AWAITING_MS);
        }, step.durationMs);
        return () => {
            if (showTimerRef.current)
                clearTimeout(showTimerRef.current);
            if (awaitingTimerRef.current)
                clearTimeout(awaitingTimerRef.current);
        };
    }, [phase, step]);
    const handleTap = () => {
        if (phase !== 'showing' && phase !== 'awaiting')
            return;
        if (showTimerRef.current)
            clearTimeout(showTimerRef.current);
        if (awaitingTimerRef.current)
            clearTimeout(awaitingTimerRef.current);
        setTapped(true);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPhase('showing');
        setTapped(false);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('freeze_frame.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { className: "flex items-center justify-center", style: { width: FIELD, height: FIELD, background: '#1a1a1a' }, children: [phase === 'showing' && (_jsx("span", { className: "select-none font-display text-[58px] text-cyan-surface", children: step.glyph })), phase === 'awaiting' && (_jsx("span", { className: "font-display text-[14px] text-cyan-surface", children: t('freeze_frame.demo.awaiting_hint') })), phase === 'feedback' && (_jsx("span", { className: [
                            'font-display text-[40px]',
                            correct ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: correct ? '✓' : '✗' }))] }), (phase === 'showing' || phase === 'awaiting') && (_jsx("button", { type: "button", onClick: handleTap, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('freeze_frame.tap_button') })), phase === 'feedback' && (_jsx("p", { className: [
                    'text-[13px]',
                    correct ? 'text-cyan-core' : 'text-cyan-media',
                ].join(' '), children: correct
                    ? t('freeze_frame.demo.feedback_correct')
                    : t('freeze_frame.demo.feedback_incorrect') })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map